import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../Modal';
import { GameState, Player, apiService } from '../../../services/api';
import EditButton from './Buttons/EditButton';
import DeleteButton from './Buttons/DeleteButton';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  gameState: GameState;
  currentPlayerId: string;
  onGameStateUpdate: (gameState: GameState) => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose,
  gameState,
  currentPlayerId,
  onGameStateUpdate
}) => {
  const { t } = useTranslation();
  const [editingPlayerId, setEditingPlayerId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isHost = gameState.isHost;

  const handleEditPlayerName = (player: Player) => {
    setEditingPlayerId(player.id);
    setEditingName(player.name);
  };

  const handleCancelEdit = () => {
    setEditingPlayerId(null);
    setEditingName('');
    setError(null);
  };

  const handleSaveName = async (currentPlayerId:string, targetPlayerId: string, newName: string) => {
    setLoading(true);
    setError(null);

    try {
      const result = await apiService.changePlayerName(
        gameState.gameCode,
        targetPlayerId,
        currentPlayerId,
        newName.trim()
      );

      if (result.success && result.gameState) {
        onGameStateUpdate(result.gameState);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change name');
    } finally {
      setLoading(false);
      handleCancelEdit()
    }
  };

  useEffect(() => {
    if (editingPlayerId) {
      const input = window.prompt(t("settings.editName"), editingName)
      
      if (input !== null && input !== "") {
        handleSaveName(currentPlayerId, editingPlayerId, input)
      }
    }
  }, [currentPlayerId, editingPlayerId, editingName])

  const handleRemovePlayer = async (playerId: string) => {
    if (!window.confirm(t('settings.confirmRemovePlayer'))) return;

    setLoading(true);
    setError(null);

    try {
      const result = await apiService.removePlayer(
        gameState.gameCode,
        playerId,
        currentPlayerId
      );

      if (result.success && result.gameState) {
        onGameStateUpdate(result.gameState);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove player');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('settings.title')}
      contentClassName="max-w-lg"
    >
      <div className="space-y-6">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Players Management Section */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-800">{t('settings.gamePlayers')}</h3>
          <div className="space-y-2">
            {gameState.players.map((player) => (
              <div key={player.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <span className="flex-1 font-medium">
                  {player.name}
                  {player.id === currentPlayerId && (
                    <span className="ml-2 text-sm text-blue-600 font-medium">{t('settings.you')}</span>
                  )}
                </span>
                {(isHost || player.id === currentPlayerId) && (
                  <div className="flex gap-2">
                    <EditButton onClick={() => handleEditPlayerName(player)} disabled={loading}/>
                    {(isHost && player.id !== currentPlayerId) && <DeleteButton onClick={() => handleRemovePlayer(player.id)} disabled={loading}/>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {loading && (
          <div className="text-center text-gray-600">
            {t('settings.loading')}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SettingsModal;